/**
 * i18n key for the staff/teamlist "Orders" tab label (sidebar nav item + page breadcrumb).
 * Deliberately its own key — NOT `SIDBAR_PERSONALORDER_INVOICE`, which the member-facing
 * personalorder Invoices tab keeps unchanged.
 */
export const TEAMLIST_ORDERS_LABEL_KEY = 'TEXT_ORDERS'

// Quick expiry-window presets for the team Subscriptions filter. Each returns a concrete {start, end}
// Date pair so the filter never relies on a free-typed date (the source of the #14100 empty-result
// bug) and a leader can pick "who expires in the next 30 days" in one click.
export type ExpiryPresetKey =
  | 'next7'
  | 'next30'
  | 'next90'
  | 'thisMonth'
  | 'nextMonth'
  | 'expired14'

export function expiryPreset(key: ExpiryPresetKey): {start: Date; end: Date} {
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth()
  const d = now.getDate()
  const dayStart = new Date(y, m, d, 0, 0, 0)
  const addDays = (base: Date, n: number) => new Date(base.getFullYear(), base.getMonth(), base.getDate() + n, 0, 0, 0)
  switch (key) {
    case 'next7':
      return {start: dayStart, end: addDays(dayStart, 7)}
    case 'next30':
      return {start: dayStart, end: addDays(dayStart, 30)}
    case 'next90':
      return {start: dayStart, end: addDays(dayStart, 90)}
    case 'thisMonth':
      return {start: new Date(y, m, 1, 0, 0, 0), end: new Date(y, m + 1, 0, 23, 59, 59)}
    case 'nextMonth':
      return {start: new Date(y, m + 1, 1, 0, 0, 0), end: new Date(y, m + 2, 0, 23, 59, 59)}
    case 'expired14':
      return {start: addDays(dayStart, -14), end: new Date(y, m, d, 23, 59, 59)}
  }
}

// Wide bounds used for the "Any" preset — effectively "no date restriction" while still sending a
// valid, parseable date pair (so we never hit the Invalid-Date -> NULL empty-result path, #14100).
export const WIDE_START = new Date(2000, 0, 1, 0, 0, 0)
export const WIDE_END = new Date(2100, 0, 1, 0, 0, 0)

// Signup-date ("Joined") presets for the team Subscriptions filter.
export type CreatePresetKey = 'thisMonth' | 'last30'

export function createPreset(key: CreatePresetKey): {start: Date; end: Date} {
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth()
  const d = now.getDate()
  if (key === 'thisMonth') {
    return {start: new Date(y, m, 1, 0, 0, 0), end: new Date(y, m + 1, 0, 23, 59, 59)}
  }
  // last30
  return {start: new Date(y, m, d - 30, 0, 0, 0), end: new Date(y, m, d, 23, 59, 59)}
}

export function getNextMonthStart() {
  const today = new Date()
  const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1)
  return formatDate(nextMonth, '00:00:00')
}

export function getNextMonthEnd() {
  const today = new Date()
  const nextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 1)
  return formatDate(nextMonth, '00:00:00')
}

function formatDate(date: Date, time: string) {
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd} ${time}`
}

export function dateFormatterNoUTC(date: string) {
  if (!date) return new Date()

  let parts = date.replace(' ', 'T').split(/[-T:.Z]/)
  if (parts.length < 6) {
    return new Date()
  }

  return new Date(
    parseInt(parts[0], 10),
    parseInt(parts[1], 10) - 1,
    parseInt(parts[2], 10),
    parseInt(parts[3], 10),
    parseInt(parts[4], 10),
    parseInt(parts[5], 10)
  )
}
